import BaseHeader from "./BaseHeader";
import BaseBody from "./BaseBody";
import { ColumnDef } from "types/common/Header";
import { ORDER_BY } from "pages/admin-app/adminapp.constants";
import { useEffect, useRef } from "react";

interface Props<T extends { id: number }> {
  headers: ColumnDef<T>[];
  data: T[];
  entityType: string;
  onDelete: (entityId: string) => void;
  sortBy: string;
  order: ORDER_BY;
  onSort: (key: string) => void;
}

const BaseTable = <T extends { id: number }>({
  headers,
  data,
  entityType,
  onDelete,
  sortBy,
  order,
  onSort,
}: Props<T>) => {
  const tableRef = useRef<HTMLDivElement | null>(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);

  useEffect(() => {
    const el = tableRef.current;
    if (!el) return;

    const handleMouseDown = (e: globalThis.MouseEvent) => {
      isDragging.current = true;
      el.classList.add("grabbing");
      startX.current = e.pageX - el.offsetLeft;
      scrollLeft.current = el.scrollLeft;
    };

    const handleMouseLeave = () => {
      isDragging.current = false;
      el.classList.remove("grabbing");
    };

    const handleMouseUp = () => {
      isDragging.current = false;
      el.classList.remove("grabbing");
    };

    const handleMouseMove = (e: globalThis.MouseEvent) => {
      if (!isDragging.current) return;
      e.preventDefault();
      const x = e.pageX - el.offsetLeft;
      const walk = x - startX.current;
      el.scrollLeft = scrollLeft.current - walk;
    };

    el.addEventListener("mousedown", handleMouseDown);
    el.addEventListener("mouseleave", handleMouseLeave);
    el.addEventListener("mouseup", handleMouseUp);
    el.addEventListener("mousemove", handleMouseMove);

    return () => {
      el.removeEventListener("mousedown", handleMouseDown);
      el.removeEventListener("mouseleave", handleMouseLeave);
      el.removeEventListener("mouseup", handleMouseUp);
      el.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  return (
    <div className="table-wrapper" ref={tableRef}>
      <table className="table">
        <BaseHeader
          headers={headers}
          sortedKey={sortBy}
          sortedOrder={order}
          onSort={onSort}
        />
        <BaseBody
          headers={headers}
          data={data}
          entityType={entityType}
          onDelete={onDelete}
        />
      </table>
    </div>
  );
};

export default BaseTable;
